// src/ConsumerApp/Screens/Home/Home.js

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar, CenteredContainer, Footer } from '../../DesignSystem';
import { theme } from '../../../assets/theme/theme';
import Bucket from '../Bucket/Bucket';

// SVG Icons
import ToteIcon from '../../../assets/icons/tote.svg';

// Local Banner Assets
import Hero1Img from './images/hero1.jpeg';
import Hero2Img from './images/hero2.jpeg';
import Offer1Img from './images/offer1.jpeg';
import BuildPointsImg from './images/buildpoints.jpeg';
import Offer2Img from './images/offer2.jpeg';
import Offer1000Img from './images/1000offer.jpeg';

// Brand Image Assets
import UltratechCementImg from '../../../assets/images/Brands/UltratechCement.jpeg';
import DalmiaCementImg from '../../../assets/images/Brands/DalmiaBarathCement.jpg';
import AsianPaintsImg from '../../../assets/images/Brands/asianPaints.jpg';
import FinolexImg from '../../../assets/images/Brands/Finolex.jpg';
import SupremePlasticsImg from '../../../assets/images/Brands/SupremePlastics.jpg';
import MYKArmentImg from '../../../assets/images/Brands/MYKArment.jpg';
import RRKabel from '../../../assets/images/Brands/RR.png';
import Goldmedal from '../../../assets/images/Brands/Goldmedal.png';
import kcp from '../../../assets/images/Brands/kcp.jfif';
import krafto from '../../../assets/images/Brands/kraftoLogo.png';
import simpolo from '../../../assets/images/Brands/Simpolo.png';
import siemens from '../../../assets/images/Brands/siemens.png';

// Shared API Hooks
import { useGetCategories } from '../../api/categories/category.hooks';
import {
  useGetFeaturedProducts,
  useFrequentlyBoughtProducts,
} from '../../api/products/product.hooks';
import { useGetOrdersByCustomer } from '../../api/orders/orders.hooks';
import LiveOrderTrackerBanner from './components/LiveOrderTrackerBanner';
import BuilderTestimonials from './components/BuilderTestimonials';
import FeaturedCategoriesSection from './components/FeaturedCategoriesSection';
import { useAuthModal } from '../../context/AuthModalContext';

const IMAGE_BASE_URL = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';
const BUSINESS_WHATSAPP_NUMBER = '918639065454';

const POPULAR_BRANDS = [
  { id: '1', name: 'UltraTech', label: 'UltraTech\nCEMENT', image: UltratechCementImg },
  { id: '2', name: 'Dalmia', label: 'Dalmia\ncement', image: DalmiaCementImg },
  { id: '3', name: 'Asian Paints', label: 'ap\nasianpaints', image: AsianPaintsImg },
  { id: '4', name: 'Finolex', label: 'Finolex\nPIPES', image: FinolexImg },
  { id: '5', name: 'Supreme', label: 'Supreme\nPlastics', image: SupremePlasticsImg },
  { id: '6', name: 'MYK Arment', label: 'MYK\narment', image: MYKArmentImg },
  { id: '7', name: 'RR Kabel', label: 'RR\nKabel', image: RRKabel },
  { id: '8', name: 'Gold Medal', label: 'Gold\nMedal', image: Goldmedal },
  { id: '9', name: 'KCP', label: 'KCP\nCement', image: kcp },
  { id: '10', name: 'Krafto', label: 'Krafto\nPipes', image: krafto },
  { id: '11', name: 'siemens', label: 'Siemens\nSwitchgear', image: siemens },
  { id: '12', name: 'simpolo', label: 'Simpolo\nCeramics', image: simpolo },
];

function CategoryCardImage({ imageUri }) {
  const [hasError, setHasError] = useState(false);

  if (!imageUri || hasError) {
    return (
      <div className="bb-cat-fallback">
        <img src={ToteIcon} alt="fallback" className="bb-cat-fallback-icon" />
      </div>
    );
  }

  return (
    <img
      src={imageUri}
      alt="Category"
      className="bb-cat-img"
      onError={() => setHasError(true)}
      loading="lazy"
    />
  );
}

function RecentItemImage({ imageUri }) {
  const [hasError, setHasError] = useState(false);

  if (!imageUri || hasError) {
    return (
      <div className="bb-recent-fallback">
        <img src={ToteIcon} alt="fallback" style={{ width: 22, height: 22, opacity: 0.35 }} />
      </div>
    );
  }

  return (
    <img
      src={imageUri}
      alt="Product"
      className="bb-recent-img"
      onError={() => setHasError(true)}
      loading="lazy"
    />
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isBucketOpen, setIsBucketOpen] = useState(false);
  const { openLoginModal } = useAuthModal();

  useEffect(() => {
    const pending = sessionStorage.getItem('pendingDeepLink');
    const searchParams = new URLSearchParams(window.location.search);
    
    // If there is a pending link OR an rfq_id in the URL, prompt login
    if (pending || searchParams.get('rfq_id')) {
      setTimeout(() => openLoginModal(), 150);
    }
  }, [openLoginModal]);

  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id;

  const { data: rawCategories, isLoading: isCategoriesLoading, isError: isCategoriesError } = useGetCategories();
  const { data: rawMostSearched, isLoading: isProductsLoading } = useFrequentlyBoughtProducts();
  const { data: ordersData } = useGetOrdersByCustomer(customerId);
  const { data: featuredData } = useGetFeaturedProducts();

  const categories = useMemo(() => {
    const list = Array.isArray(rawCategories) ? rawCategories : rawCategories?.data || [];
    return list.map((cat) => ({
      id: cat._id || cat.id,
      name: cat.product_category_name || cat.name || '',
      image: cat.product_category_image ? `${IMAGE_BASE_URL}${cat.product_category_image}` : null,
    }));
  }, [rawCategories]);

  const mostBoughtList = useMemo(() => {
    const list = Array.isArray(rawMostSearched) ? rawMostSearched : rawMostSearched?.data || [];
    return list;
  }, [rawMostSearched]);

  const featuredCategorySections = useMemo(() => {
    return Array.isArray(featuredData) ? featuredData : featuredData?.data || [];
  }, [featuredData]);

  const activeLiveOrders = useMemo(() => {
    const all = ordersData?.orders || ordersData || [];
    if (!Array.isArray(all)) return [];
    const activeStatuses = ['Pending', 'Confirmed', 'Delivering'];
    return all.filter((o) => activeStatuses.includes(o.salesorder_status) || o.is_live_order);
  }, [ordersData]);

  const handleOpenWhatsAppRFQ = () => {
    const message = encodeURIComponent('Hi, I would like to request a quote (RFQ) for my material list.');
    window.open(`https://wa.me/${BUSINESS_WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div style={styles.pageShell}>
      {/* Dynamic CSS Grid System for Responsive Scaling */}
      <style>{`
        /* ── Container Layout & Spacing ── */
        .bb-home-feed {
          display: flex;
          flex-direction: column;
          gap: 28px;
          width: 100%;
        }

        .bb-section-header {
          display: flex;
          justifyContent: space-between;
          align-items: center;
          margin-bottom: 14px;
        }

        .bb-section-title {
          font-family: ${theme.typography.headingMD.fontFamily};
          font-size: clamp(15px, 2vw, 18px);
          font-weight: 800;
          color: #2B2A29;
          margin: 0;
          letter-spacing: -0.2px;
        }

        /* ── 1. Top Dual Hero Banners ── */
        .bb-hero-banners-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          width: 100%;
        }

        .bb-hero-banner-item {
          position: relative;
          width: 100%;
          aspect-ratio: 16 / 7.5;
          border-radius: 14px;
          overflow: hidden;
          cursor: pointer;
          border: 1px solid #E2E8F0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s ease;
          background-color: #F8FAFC;
        }

        .bb-hero-banner-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }

        .bb-hero-banner-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        /* ── 2. Category Adaptive Grid ── */
        .bb-categories-grid {
          display: grid;
          grid-template-columns: repeat(8, 1fr);
          gap: 16px;
          width: 100%;
        }

        .bb-category-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          cursor: pointer;
          user-select: none;
          transition: transform 0.16s ease;
        }

        .bb-category-card:hover {
          transform: translateY(-2px);
        }

        .bb-cat-img-box {
          width: 100%;
          aspect-ratio: 1 / 1;
          background-color: #F4F5F7;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px;
          box-sizing: border-box;
          overflow: hidden;
          border: 1px solid #EAECEF;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
          transition: border-color 0.15s ease;
        }

        .bb-category-card:hover .bb-cat-img-box {
          border-color: #CBD5E1;
        }

        .bb-cat-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.04));
        }

        .bb-cat-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .bb-cat-fallback-icon {
          width: 32px;
          height: 32px;
          opacity: 0.35;
        }

        .bb-cat-name {
          margin-top: 8px;
          font-size: clamp(11.5px, 1.2vw, 13px);
          line-height: 16px;
          font-weight: 700;
          color: #2B2A29;
          text-align: center;
          width: 100%;
          white-space: normal;
          overflow-wrap: break-word;
        }

        /* ── 3. Offers & Rewards 4-Card Responsive Grid ── */
        .bb-offers-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          width: 100%;
        }

        .bb-offer-card-item {
          position: relative;
          width: 100%;
          aspect-ratio: 4 / 5;
          border-radius: 14px;
          overflow: hidden;
          cursor: pointer;
          border: 1px solid #E2E8F0;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
          transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s ease;
          background-color: #FFFFFF;
        }

        .bb-offer-card-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.09);
        }

        .bb-offer-card-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        /* ── 4. Frequently Bought Horizontal Carousel ── */
        .bb-frequently-bought-wrapper {
          position: relative;
          width: 100%;
          display: flex;
          align-items: center;
        }

        .bb-frequently-bought-container {
          display: flex;
          overflow-x: auto;
          overflow-y: hidden;
          gap: 16px;
          width: 100%;
          padding: 4px 2px 14px 2px;
          scroll-behavior: smooth;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          scroll-snap-type: x mandatory;
        }

        .bb-frequently-bought-container::-webkit-scrollbar {
          display: none;
        }

        .bb-frequently-bought-card {
          width: 164px;
          min-width: 164px;
          background-color: #FFFFFF;
          border-radius: 12px;
          border: 0.5px solid #CBD5E1;
          overflow: hidden;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          scroll-snap-align: start;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          box-sizing: border-box;
        }

        .bb-frequently-bought-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 14px rgba(0, 0, 0, 0.06);
        }

        .bb-recent-img {
          width: 100%;
          height: 94px;
          object-fit: contain;
          margin-top: 8px;
          display: block;
        }

        .bb-recent-fallback {
          width: 100%;
          height: 94px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 8px;
        }

        .bb-product-body {
          padding: 8px 12px 12px 12px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .bb-product-title {
          font-size: 12.5px;
          line-height: 16px;
          color: #2B2A29;
          font-weight: 600;
          height: 32px;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .bb-product-price-row {
          display: flex;
          align-items: baseline;
          gap: 6px;
          margin-top: 2px;
        }

        .bb-product-selling-price {
          font-size: 13.5px;
          color: #2B2A29;
          font-weight: 800;
        }

        .bb-product-mrp-price {
          font-size: 11px;
          color: #94A3B8;
          text-decoration: line-through;
        }

        .carousel-nav-btn {
          position: absolute;
          top: calc(50% - 10px);
          transform: translateY(-50%);
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background-color: #FFFFFF;
          border: 1px solid #E2E8F0;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          transition: background-color 0.15s ease, transform 0.15s ease;
        }

        .carousel-nav-btn:hover {
          background-color: #F8FAFC;
          transform: translateY(-50%) scale(1.06);
        }

        .carousel-nav-prev {
          left: -14px;
        }

        .carousel-nav-next {
          right: -14px;
        }

        /* ── 5. Brands Marquee Track ── */
        .brands-marquee-viewport {
          width: 100%;
          overflow: hidden;
          position: relative;
          padding: 8px 0 14px 0;
          mask-image: linear-gradient(to right, transparent, #000000 6%, #000000 94%, transparent);
          -webkit-mask-image: linear-gradient(to right, transparent, #000000 6%, #000000 94%, transparent);
        }

        .brands-marquee-track {
          display: flex;
          align-items: center;
          gap: 16px;
          width: max-content;
          animation: continuousScroll 26s linear infinite;
          will-change: transform;
        }

        .brands-marquee-viewport:hover .brands-marquee-track {
          animation-play-state: paused;
        }

        @keyframes continuousScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .brand-logo-card {
          width: 124px;
          min-width: 124px;
          height: 76px;
          background-color: #FFFFFF;
          border-radius: 12px;
          border: 1px solid #E2E8F0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px 14px;
          cursor: pointer;
          flex-shrink: 0;
          box-sizing: border-box;
          transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
        }

        .brand-logo-card:hover {
          transform: translateY(-2px);
          border-color: #CBD5E1;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .brand-logo-img {
          width: 100%;
          height: 100%;
          max-width: 90px;
          max-height: 48px;
          object-fit: contain;
          display: block;
        }

        /* ═════════════════════════════════════════════
           RESPONSIVE BREAKPOINT ORCHESTRATION 
           ═════════════════════════════════════════════ */

        /* LAPTOP & SMALL DESKTOP (<= 1200px) */
        @media (max-width: 1200px) {
          .bb-categories-grid {
            grid-template-columns: repeat(6, 1fr);
            gap: 14px;
          }
        }

        /* TABLET PORTRAIT & LANDSCAPE (<= 992px) */
        @media (max-width: 992px) {
          .bb-home-feed {
            gap: 22px;
          }
          .bb-hero-banners-row {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          .bb-hero-banner-item {
            aspect-ratio: 16 / 7;
          }
          .bb-categories-grid {
            grid-template-columns: repeat(5, 1fr);
            gap: 12px;
          }
          .bb-offers-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
          .carousel-nav-btn {
            display: none;
          }
        }

        /* MOBILE DEVICES (<= 640px) */
        @media (max-width: 640px) {
          .bb-categories-grid {
            grid-template-columns: repeat(4, 1fr);
            column-gap: 10px;
            row-gap: 14px;
          }
          .bb-cat-img-box {
            border-radius: 12px;
            padding: 6px;
          }
          .bb-cat-name {
            font-size: 11px;
            line-height: 14px;
            margin-top: 6px;
          }
          .bb-hero-banner-item {
            aspect-ratio: 16 / 8;
            border-radius: 12px;
          }
          .bb-frequently-bought-card {
            width: 142px;
            min-width: 142px;
          }
          .bb-recent-img {
            height: 80px;
          }
        }

        /* COMPACT SCREENS (<= 480px) */
        @media (max-width: 480px) {
          .bb-home-feed {
            gap: 18px;
          }
          .bb-offers-grid {
            display: flex;
            overflow-x: auto;
            gap: 12px;
            padding-bottom: 4px;
            scrollbar-width: none;
            -webkit-overflow-scrolling: touch;
            scroll-snap-type: x mandatory;
          }
          .bb-offers-grid::-webkit-scrollbar {
            display: none;
          }
          .bb-offer-card-item {
            min-width: 220px;
            max-width: 220px;
            flex-shrink: 0;
            scroll-snap-align: start;
          }
        }
      `}</style>

      {/* Universal Navigation Bar */}
      <Navbar searchQuery={searchQuery} onSearchChange={setSearchQuery} />

      <main style={styles.mainFeedShell}>
        <CenteredContainer>
          <div className="bb-home-feed">

            {/* LIVE ORDER BANNER */}
            {activeLiveOrders && activeLiveOrders.length > 0 && (
              <LiveOrderTrackerBanner activeOrder={activeLiveOrders[0]} />
            )}

            {/* 1. TOP DUAL HERO BANNERS */}
            <div className="bb-hero-banners-row">
              <div
                className="bb-hero-banner-item"
                onClick={() => navigate('/consumer/account/delivery')}
                title="Fast Site Delivery (30-90 mins)"
              >
                <img
                  src={Hero1Img}
                  alt="Need it urgently? 30-90 mins delivery"
                  className="bb-hero-banner-img"
                />
              </div>

              <div
                className="bb-hero-banner-item"
                onClick={handleOpenWhatsAppRFQ}
                title="Request for Quote on WhatsApp"
              >
                <img
                  src={Hero2Img}
                  alt="Got bulk material requirement? Share on WhatsApp"
                  className="bb-hero-banner-img"
                />
              </div>
            </div>

            {/* 2. BROWSE BY CATEGORY */}
            <section style={styles.sectionWrap}>
              <div className="bb-section-header">
                <h3 className="bb-section-title">Browse by Category</h3>
              </div>

              {isCategoriesLoading ? (
                <div style={styles.stateContainer}>Loading categories...</div>
              ) : isCategoriesError ? (
                <div style={styles.errorText}>Failed to load categories.</div>
              ) : (
                <div className="bb-categories-grid">
                  {categories.map((item) => (
                    <div
                      key={item.id}
                      className="bb-category-card"
                      onClick={() =>
                        navigate('/consumer/productslist', {
                          state: { categoryId: item.id, categoryName: item.name },
                        })
                      }
                    >
                      <div className="bb-cat-img-box">
                        <CategoryCardImage imageUri={item.image} />
                      </div>
                      <span className="bb-cat-name" title={item.name}>
                        {item.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* 3. OFFERS & REWARDS PROMO GRID */}
            <section style={styles.sectionWrap}>
              <div className="bb-offers-grid">
                <div
                  className="bb-offer-card-item"
                  onClick={() => navigate('/consumer/search?q=sentriq')}
                  title="Sentriq Metal Boxes starting ₹29"
                >
                  <img
                    src={Offer1Img}
                    alt="Sentriq Concealed Metal Boxes"
                    className="bb-offer-card-img"
                  />
                </div>

                <div
                  className="bb-offer-card-item"
                  onClick={() => navigate('/consumer/account/brick-points')}
                  title="Brick Points Rewards"
                >
                  <img
                    src={BuildPointsImg}
                    alt="Brick Points for Construction Professionals"
                    className="bb-offer-card-img"
                  />
                </div>

                <div
                  className="bb-offer-card-item"
                  onClick={() => navigate('/consumer/search?q=cement')}
                  title="Cement Offer - Up to ₹500 off"
                >
                  <img
                    src={Offer2Img}
                    alt="Up to ₹500 OFF on your first cement order"
                    className="bb-offer-card-img"
                  />
                </div>

                <div
                  className="bb-offer-card-item"
                  onClick={() => navigate('/consumer/account/offers')}
                  title="Welcome Offer - Up to ₹1000 in savings"
                >
                  <img
                    src={Offer1000Img}
                    alt="Get up to ₹1000 in savings"
                    className="bb-offer-card-img"
                  />
                </div>
              </div>
            </section>

            {/* 4. FREQUENTLY BOUGHT BY BUYERS */}
            {!isProductsLoading && mostBoughtList.length > 0 && (
              <section style={styles.sectionWrap}>
                <div className="bb-section-header">
                  <h3 className="bb-section-title">Frequently bought by buyers</h3>
                </div>

                <div className="bb-frequently-bought-wrapper">
                  <button
                    className="carousel-nav-btn carousel-nav-prev"
                    type="button"
                    aria-label="Scroll left"
                    onClick={() => {
                      const container = document.getElementById('frequently-bought-scroll');
                      if (container) container.scrollBy({ left: -340, behavior: 'smooth' });
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2B2A29" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </button>

                  <div id="frequently-bought-scroll" className="bb-frequently-bought-container">
                    {mostBoughtList.map((item) => {
                      const productId = item.product_id || item._id;
                      const name = item.product_name || item.name || 'Unknown Product';
                      const imgPath = item.product_image || item.product_image_url || item.image;
                      const buyNowPrice =
                        item?.buy_now?.unit_price ||
                        item?.selling_price ||
                        item?.unit_price ||
                        item?.price;
                      const mrp = item?.mrp;

                      return (
                        <div
                          key={productId}
                          className="bb-frequently-bought-card"
                          onClick={() =>
                            navigate('/consumer/productdetails', { state: { productId } })
                          }
                        >
                          <RecentItemImage
                            imageUri={imgPath ? `${IMAGE_BASE_URL}${imgPath}` : null}
                          />

                          <div className="bb-product-body">
                            <p className="bb-product-title" title={name}>
                              {name}
                            </p>

                            <div className="bb-product-price-row">
                              <span className="bb-product-selling-price">
                                ₹{Number(buyNowPrice).toLocaleString('en-IN')}
                              </span>
                              {mrp && Number(mrp) > Number(buyNowPrice) && (
                                <span className="bb-product-mrp-price">
                                  ₹{Number(mrp).toLocaleString('en-IN')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    className="carousel-nav-btn carousel-nav-next"
                    type="button"
                    aria-label="Scroll right"
                    onClick={() => {
                      const container = document.getElementById('frequently-bought-scroll');
                      if (container) container.scrollBy({ left: 340, behavior: 'smooth' });
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2B2A29" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              </section>
            )}

            {/* 5. FEATURED CATEGORY PRODUCTS */}
            {featuredCategorySections.length > 0 && (
              <FeaturedCategoriesSection categorySections={featuredCategorySections} />
            )}

            {/* 6. OUR TRUSTED BRANDS */}
            <section style={styles.sectionWrap}>
              <div className="bb-section-header">
                <h3 className="bb-section-title">Our Trusted Brands</h3>
              </div>

              <div className="brands-marquee-viewport">
                <div className="brands-marquee-track">
                  {[...POPULAR_BRANDS, ...POPULAR_BRANDS].map((brand, index) => (
                    <div
                      key={`${brand.id}-${index}`}
                      className="brand-logo-card"
                      onClick={() =>
                        navigate(`/consumer/search?q=${encodeURIComponent(brand.name)}`)
                      }
                      title={brand.name}
                    >
                      <img
                        src={brand.image}
                        alt={brand.name}
                        className="brand-logo-img"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* 7. BUILDER TESTIMONIALS */}
            <BuilderTestimonials />

          </div>
        </CenteredContainer>
      </main>

      <Footer />

      <Bucket isOpen={isBucketOpen} onClose={() => setIsBucketOpen(false)} />
    </div>
  );
}

const styles = {
  pageShell: {
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    color: '#2B2A29',
  },
  mainFeedShell: {
    width: '100%',
    padding: '20px 0 60px 0',
  },
  sectionWrap: {
    width: '100%',
  },
  stateContainer: {
    padding: '24px 0',
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 13,
  },
  errorText: {
    color: '#E9001E',
    padding: '16px 0',
    textAlign: 'center',
    fontSize: 13,
  },
};